<template>
    <div class="uploaded-files-card sl-card">
        <!-- Title -->
        <div class="sl-card__title">
            <span>{{ t('card.uploaded_files.title') }}</span>

            <span
                v-if="files.length"
                class="uploaded-files-card__count"
            >{{ files.length }}</span>
        </div>

        <div v-if="loading" class="flex justify-center items-center">
            <span class="loader"></span>
        </div>

        <!-- Card Content -->
        <div v-else class="space-y-1 text-xs">
            <!-- File List -->
            <template v-if="files.length">
                <lsn-loader-link
                    v-for="{ name, type } in files"
                    :key="name"
                    :is-loading="downloading[name]"
                    @click="download(order.order_by, order.fitting_session_id, name)"
                >
                    <div class="flex justify-between gap-1 w-full">
                        <div class="grow truncate">
                            {{ name }}
                        </div>

                        <div v-if="type" class="shrink-0 text-gray-500">
                            {{ getType(type) }}
                        </div>
                    </div>
                </lsn-loader-link>
            </template>

            <div v-else class="text-gray-500">
                {{ t('card.uploaded_files.no_files') }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import axios from '@/axios';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import LsnLoaderLink from '@/components/LsnLoaderLink.vue';

// ------------------------------------------------------------ PROPS

const props = defineProps({
    order:
    {
        type:     Object,
        required: true,
    },
});

// ------------------------------------------------------------ COMPOSABLES

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();

// ------------------------------------------------------------ DATA

const loading = ref(false);
const downloading = ref({});
const files = ref([]);

// ------------------------------------------------------------ METHODS

async function fetchFiles()
{
    if(loading.value)
    {
        return;
    }

    loading.value = true;
    const eid = props.order.order_by;
    const fid = props.order.fitting_session_id;
    const url = `/api/fitting-session/${eid}/${fid}/file`;

    try
    {
        const { data } = await axios.get(url);
        files.value = data;
    }
    catch(error)
    {
        if(error.response?.status !== 404)
        {
            console.error('error retrieving uploads', { error });
            toast.error(t('parameters.UPLOAD_FILES.errors.cannot_load_files'));
        }

        files.value = [];
    }
    finally
    {
        loading.value = false;
    }
}

/**
 * Trigger the download of an uploaded file.
 *
 * @async
 * @param {string} eid  The ID of the entity.
 * @param {string} fid  The ID of the fitting session.
 * @param {string} name The filename.
 * @returns {Promise<void>}
 */
async function download(eid, fid, name)
{
    if(downloading.value[name])
    {
        return;
    }

    downloading.value[name] = true;
    const url = `/api/fitting-session/${eid}/${fid}/file/${name}`;

    try
    {
        const response = await axios.get(url, { responseType: 'blob' });
        const blob = new Blob(
            [response.data],
            { type: response.headers['content-type'] }
        );
        const href = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = name;
        a.click();
    }
    catch(error)
    {
        toast.error(t('parameters.UPLOAD_FILES.errors.cannot_download_file'));
        console.error('error downloading file', { eid, fid, name, error });
    }
    finally
    {
        delete downloading.value[name];
    }
}

function getType(type)
{
    return t(`parameters.UPLOAD_FILES.file_type.${type}.label`);
}

// ------------------------------------------------------------ LIFECYCLE HOOKS

onMounted(fetchFiles);
</script>

<style scoped>
@reference "#main.css";

.sl-card__title
{
    & > span:nth-child(1)
    {
        @apply inline-block align-middle;
    }

    & > span:nth-child(2)
    {
        @apply cursor-default inline-flex justify-center items-center
            align-middle ml-2 rounded-full w-4 h-4
            bg-primary-50 text-primary-600 text-xs;
    }
}

:deep(.lsn-loader-link, .lsn-loader-link__label)
{
    @apply block;
}
</style>
